import {
  type ExperimentAssignmentState,
  useExperimentAssignment,
} from 'components/helpers/useExperimentAssignment';

export const AVAILABLE_SOON_EMAIL_CAPTURE_FLAG = 'growth-26-10-available-soon-email-capture-cta';

// Variant serves GrowthBook booleans as 'true'; the localStorage QA override
// normalizes to 'on'.
const flagIsOn = (variant: string) => variant === 'true' || variant === 'on';

export type AvailableSoonEmailCapture = Pick<
  ExperimentAssignmentState,
  'isExperimentAssignment' | 'trackExposure'
> & {
  /** Whether the email-capture card may render. False until the assignment resolves. */
  enabled: boolean;
};

/**
 * The email-capture card's GrowthBook assignment. Reads through the experiment
 * path so experiment-backed serves can report exposure; a plain BFF flag read
 * records none, so an experiment gated on it would run blind. QA overrides
 * via localStorage `opendoor.cosmos.<flag>` still win.
 *
 * Takes `enabled` (the PDP is coming-soon) both as the experiment's eligibility
 * — an ineligible viewer is never bucketed — and as the card gate: an
 * ineligible PDP renders no card even once an assignment resolved earlier in
 * the session. Fails closed while a read is unresolved; a resolved serve
 * renders per the flag value, while only experiment-backed serves enter the
 * exposure stream.
 */
export const useAvailableSoonEmailCapture = (enabled: boolean): AvailableSoonEmailCapture => {
  const assignment = useExperimentAssignment({
    experimentKey: AVAILABLE_SOON_EMAIL_CAPTURE_FLAG,
    enabled,
    honorLocalOverride: true,
  });

  return {
    enabled: enabled && assignment.variant != null && flagIsOn(assignment.variant),
    isExperimentAssignment: assignment.isExperimentAssignment,
    trackExposure: assignment.trackExposure,
  };
};
